import { useContext, useEffect, useState } from 'react';

import { DeleteOutlined, EditOutlined, UserOutlined } from '@ant-design/icons';
import {
	Avatar,
	Button,
	Col,
	Drawer,
	Flex,
	Form,
	Input,
	Popconfirm,
	Select,
	Spin,
	Table,
	Tooltip,
	Typography,
	message,
} from 'antd';
import type { SelectProps } from 'antd';
import { useForm } from 'antd/es/form/Form';
import TextArea from 'antd/es/input/TextArea';

import { useCreateBatchProcess } from '@/api/batch_process';
import {
	useAddCategoryToMonitor,
	useAddCreatorToMonitor,
	useGetCreatorByUrl,
	useGetCreatorsTags,
	useGetMonitorCategories,
} from '@/api/monitor_collections';
import { WHISPER_ACTIONS } from '@/const';
import placeholderImage from '@/lib/assets/placeholder.jpg';
import isValidUrl from '@/lib/helpers/isValidUrl';
import prepareImageSrc from '@/lib/helpers/prepareImageSrc';
import Platforms from '@/pages/app/dashboard/creators/platforms';
import { IPagination } from '@/pages/app/dashboard/video-intelligence-on-demand';
import { PageContext } from '@/providers/PageContextProvider';

import columns from './data';

const layout = {
	labelCol: { span: 5 },
	wrapperCol: { span: 21 },
};

export default function AddNewCategoryDrawer() {
	const { drawer, closeDrawer } = useContext(PageContext);

	const [form] = useForm();
	const [currentId, setCurrentId] = useState<number | null>(null);
	const [pagination, setPagination] = useState<IPagination>({
		page: 1,
		pageCount: 1,
		total: 0,
		limit: 10,
	});

	const [sortBy, setSortBy] = useState<'created_at'>('created_at');
	const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
	const { data: categoriesData, isFetching: isCategoriesFetching } =
		useGetMonitorCategories({
			page: pagination.page,
			limit: pagination.limit,
			sortBy,
			sortOrder,
			enabled: drawer?.type === 'add-new-category' && drawer?.isOpen,
		});

	const { mutate: addCategoryToMonitor, isLoading: isAddToMonitorLoading } =
		useAddCategoryToMonitor(Number(drawer?.data?.id));

	const onCancelClick = () => {
		closeDrawer();
		setCurrentId(null);
		form.resetFields();
	};

	const onAddCategoryClick = (record: any) => {
		setCurrentId(record?.id);
		addCategoryToMonitor(
			{
				id: Number(drawer?.data?.id),
				category_id: record?.id,
				category_subscription_id: record?.category_subscription_id,
			},
			{
				onSuccess: () => {
					message.success('Added');
					closeDrawer();
					form.resetFields();
					setCurrentId(null);
				},
				onError: (error) => {
					const err = error as any;
					message.error(
						`An error occured: ${
							err?.response?.data?.message || err?.message
						}.`,
					);
					console.log('error', error);
					setCurrentId(null);
				},
			},
		);
	};

	const onTableChange = (__: any, _: any, sorter: any) => {
		const { field = 'created_at', order = 'ascend' } = sorter;

		if (field) {
			setSortBy(field);
			// setSearchParams((searchParams) => {
			// 	searchParams.set('sortBy', field);
			// 	return searchParams;
			// });
		}

		if (order) {
			setSortOrder(order === 'ascend' ? 'asc' : 'desc');
			// setSearchParams((searchParams) => {
			// 	searchParams.set('sortOrder', order === 'ascend' ? 'asc' : 'desc');
			// 	return searchParams;
			// });
		}
	};

	const items = categoriesData?.data?.data?.items || [];
	const total = categoriesData?.data?.data?.pagination?.total || 0;

	return (
		<Drawer
			open={drawer.isOpen && drawer.type === 'add-new-category'}
			width={'650px'}
			onClose={onCancelClick}
			title="New category"
		>
			<Col span={24}>
				<Flex
					justify="end"
					align="center"
					gap={20}
					style={{ marginBottom: '1.25rem' }}
				>
					<Button onClick={onCancelClick}>Cancel</Button>
					{/* <Button
						type="primary"
						onClick={onSaveClick}
						loading={isAddToMonitorLoading}
					>
						Save
					</Button> */}
				</Flex>
			</Col>
			<Form
				layout="vertical"
				form={form}
				initialValues={{
					link: '',
					tags: [],
				}}
				// style={{ marginBottom: "1rem" }}
			>
				<Flex
					vertical
					// gap={16}
				>
					<Table
						size="small"
						loading={isCategoriesFetching}
						// style={{ width: '100%' }}
						onChange={onTableChange}
						dataSource={items}
						columns={[
							...columns,
							{
								title: 'Actions',
								dataIndex: 'actions',
								key: 'actions',
								width: 100,
								ellipsis: false,
								fixed: 'right',
								// flex: true,
								render: (_: any, record: any) => {
									return (
										<Flex justify="center" align="center" gap={8}>
											<Button
												size="small"
												onClick={() => onAddCategoryClick(record)}
												disabled={isAddToMonitorLoading}
												loading={
													isAddToMonitorLoading && currentId === record?.id
												}
											>
												Add
											</Button>
											{/* <Tooltip title="Remove">
												<Popconfirm
													title={`Sure to remove category?`}
													onConfirm={() => onDeleteClick(Number(record?.id))}
													placement="topLeft"
												>
													<Button danger size="small">
														<DeleteOutlined />
													</Button>
												</Popconfirm>
											</Tooltip> */}
										</Flex>
									);
								},
							},
						]}
						rowKey={'id'}
						bordered
						scroll={{ x: 600 }}
						pagination={{
							total,
							showSizeChanger: true,
							current: pagination.page,
							pageSizeOptions: [10, 25, 50],
							onChange: (page: number, pageSize: number) => {
								setPagination((cur) => ({ ...cur, page, limit: pageSize }));
							},
						}}
					/>

					{/* <Form.Item
						label="Creator URL"
						name="link"
						rules={[
							{
								required: true,
								message: 'Please input creator URL',
							},
							{
								type: 'url',
								message: 'Please input a valid URL',
							},
						]}
					>
						<Input
							placeholder="https://www.instagram.com/creator/"
							prefix={<UserOutlined />}
							// onChange={(e) => setSearchUrl(e.target.value)}
							// onBlur={(e) => onBlurInput(e.target.value)}
							// value={searchUrl}
							// loading={isSearchCreatorFetching}
							disabled={isAddToMonitorLoading}
							style={{ width: '100%' }}
						/>
					</Form.Item> */}
				</Flex>
			</Form>
		</Drawer>
	);
}
